// Direct fire, fire missions, suppression, pinning, morale, and losses. See spec §4 "Combat",
// "Suppression", and "Morale", and plan Task 10. Runs once per tick, after movement and (on minute
// boundaries) detection.
//
// UNIT_DEFS: deck.ts (Task 6) does not yet export UNIT_DEFS as of this writing - only CARDS/card/
// isLegal/FAMILY_META. data/units.json is already the authoritative source, and scenario.ts and
// detection.ts each already read it directly the same way this module does. Once deck.ts exports
// UNIT_DEFS, switch this (and those two) to `import { UNIT_DEFS } from './deck'` and drop the JSON
// import below; this file does not re-export its own copy to avoid a duplicate-export collision when
// that happens.
import type { Dials, Picture, Side, Unit, UnitDef, UnitType } from './types';
import { TICKS_PER_MIN } from './types';
import type { Grid } from './grid';
import type { Rng } from './rng';
import type { EventLog } from './events';
import type { OrderBook } from './orders';
import { loadRules, type Rules } from './rules';
// eslint-disable-next-line @typescript-eslint/no-var-requires
import unitsJson from '../../data/units.json';

const UNIT_DEFS: Record<UnitType, UnitDef> = unitsJson as unknown as Record<UnitType, UnitDef>;

const DEFAULT_RULES = loadRules('realistic');

/**
 * Attacker type -> target type direct-fire damage multiplier. Anchors are exactly per the plan's
 * Task 10 spec:
 *   armor vs armor 1.0, armor vs mech 1.3 (mech is soft against a tank main gun), armor vs inf 0.8;
 *   mech vs armor 0.5 (mech's autocannon struggles against heavy armor), mech vs mech 1.0, mech vs inf 1.2;
 *   inf vs armor 0.3 (man-portable anti-tank is weak against a tank), inf vs mech 0.6, inf vs inf 1.0;
 *   recon fights with the same light cannon/anti-tank class as mech, scaled x0.6 for its thinner fit;
 *   spArty/mortar are area weapons - direct fire here is flat 0.2 regardless of target (their real
 *     damage comes from fire missions, handled separately below, not this per-tick table);
 *   hq/log are lightly-armed support vehicles - flat 0.3 regardless of target;
 *   uas carries no weapon (weaponRangeM 0 in data/units.json) and never reaches this table in
 *     practice; its row is 0 for completeness.
 * Non-anchor cells (recon/spArty/mortar/hq/log/uas as *targets*, and armor/mech/inf vs those columns):
 * recon/spArty/mortar/hq/log are lightly-armored "soft" targets (armor stat 2-3, close to infantry's 2
 * in data/units.json), so armor/mech/inf are about as effective against them as against infantry
 * (slightly better, since none of them are dug-in infantry); recon's own row scales mech's the same
 * way its anchors do (x0.6); uas as a target is a near-miss for ground direct fire (it flies at
 * heightM 500 m) so every row gives it a token 0.1 (0.06 for recon) rather than 0.
 *
 * This table (and every other tuning constant this module used to hardcode - the lethality scalar, the
 * damage split, posture/terrain/ammo multipliers, fire-mission radius/volleys/ammo, morale deltas, the
 * pin/break/destroy thresholds) now lives in `data/rules.json`, read through the `Rules` object each
 * step function takes (pitch 10, "rules as data"). `ARMOR_MULT` stays exported at its "realistic" preset
 * value for callers (and tests) that want the anchor table without resolving a full ruleset.
 */
export const ARMOR_MULT: Record<UnitType, Record<UnitType, number>> = DEFAULT_RULES.armorMult;

const STALE_CONTACT_TICKS = 10; // lastSeen must be within this many ticks of now to shoot at it
const CONTACT_IDLE_TICKS = 30; // inContact clears after this many ticks with no fire in either direction

/**
 * Per-Sim combat state: volleys fired so far per fire-mission order id, and the last tick (by either
 * side) a unit fired or was fired upon (direct-fire only). Held on the owning Sim (via `newCombatState`)
 * rather than as module-level state, so two Sims (or a replay alongside a live run) in the same process
 * never share or collide on it. `fireMissionVolleys` entries are deleted once an order completes its
 * third volley, so this never grows unbounded within a run. `lastFireTick` is used to clear `inContact`
 * after CONTACT_IDLE_TICKS ticks of neither firing nor being fired on; it is never cleared otherwise
 * (unit ids are unique for the life of a Sim).
 */
export interface CombatState {
  fireMissionVolleys: Map<number, number>;
  lastFireTick: Map<number, number>;
}

/** A fresh, empty CombatState for a new Sim (or a replay run independent of any live Sim). */
export function newCombatState(): CombatState {
  return { fireMissionVolleys: new Map(), lastFireTick: new Map() };
}

function distM(grid: Grid, a: { x: number; y: number }, b: { x: number; y: number }): number {
  return grid.distCm(a, b) / 100;
}

function markInContact(unit: Unit, t: number, state: CombatState): void {
  if (!unit.inContact) unit.contactSince = t;
  unit.inContact = true;
  state.lastFireTick.set(unit.id, t);
}

/** Nearest alive friendly HQ's cell, or the unit's own current cell if it has none. */
function nearestFriendlyHqCell(unit: Unit, units: Unit[], grid: Grid): number {
  let best: Unit | null = null;
  let bestD = Infinity;
  for (const u of units) {
    if (!u.alive || u.side !== unit.side || u.type !== 'hq') continue;
    const d = distM(grid, unit, u);
    if (d < bestD) { bestD = d; best = u; }
  }
  return best ? grid.cellOfCm(best.x, best.y) : grid.cellOfCm(unit.x, unit.y);
}

function hasFriendlyWithin1km(unit: Unit, units: Unit[], grid: Grid): boolean {
  for (const u of units) {
    if (u.id === unit.id || !u.alive || u.side !== unit.side) continue;
    if (distM(grid, unit, u) <= 1000) return true;
  }
  return false;
}

/**
 * The nearest enemy contact `unit` may engage this tick: the contact must be fresh (lastSeen within
 * STALE_CONTACT_TICKS of now) and either observed by `unit` itself or within `unit`'s own weapon range
 * (a relayed contact from a teammate); the contact is only *permission* to shoot - range and line of
 * sight are then re-checked against the enemy's real position (not the contact's, which may carry
 * position error), per the plan: "use the real unit position for LOS and range; the contact is the
 * permission to shoot."
 */
function pickTarget(t: number, unit: Unit, units: Unit[], picture: Picture, grid: Grid, weaponRangeM: number): Unit | null {
  const def = UNIT_DEFS[unit.type];
  let best: Unit | null = null;
  let bestD = Infinity;
  for (const contact of picture.contacts.values()) {
    if (contact.lastSeen < t - STALE_CONTACT_TICKS) continue;
    const enemy = units.find((u) => u.id === contact.enemyId);
    if (!enemy || !enemy.alive) continue;
    const d = distM(grid, unit, enemy);
    const permitted = contact.observerId === unit.id || d <= weaponRangeM;
    if (!permitted) continue;
    if (d > weaponRangeM) continue;
    if (d >= bestD) continue;
    const enemyDef = UNIT_DEFS[enemy.type];
    if (!grid.hasLos({ x: unit.x, y: unit.y }, def.heightM, { x: enemy.x, y: enemy.y }, enemyDef.heightM)) continue;
    best = enemy;
    bestD = d;
  }
  return best;
}

function directFirePass(t: number, units: Unit[], grid: Grid, pictures: [Picture, Picture], rng: Rng, state: CombatState, rules: Rules): void {
  const ordered = [...units].sort((a, b) => a.id - b.id);
  for (const unit of ordered) {
    if (!unit.alive || unit.ammo <= 0) continue;
    const def = UNIT_DEFS[unit.type];
    if (def.weaponRangeM <= 0) continue;

    const picture = pictures[unit.side];
    const target = pickTarget(t, unit, units, picture, grid, def.weaponRangeM);
    if (!target) continue;

    const targetCell = grid.cellOfCm(target.x, target.y);
    const armorMult = rules.armorMult[unit.type][target.type];
    const postureIn = rules.postureIncomingMult[target.posture];
    const terrainIn = 1 - grid.coverAt(targetCell) / rules.terrainDivisor;
    const ammoF = unit.ammo < rules.ammoLowThresholdCombat ? rules.ammoLowFireFactor : 1;

    const dmg = def.firepower * (unit.strength / 100) * armorMult * postureIn * terrainIn * ammoF
      * rng.range(0.7, 1.3) * rules.lethalityScalar;

    target.strength = Math.max(0, target.strength - dmg * rules.damageSplit.loss);
    target.suppression = Math.min(100, target.suppression + dmg * rules.damageSplit.suppression * rules.suppressionGainMultiplier);
    target.firedThisMin = true;

    markInContact(unit, t, state);
    markInContact(target, t, state);

    unit.ammo = Math.max(0, unit.ammo - def.ammoPerMin / 10);
  }
}

/**
 * Fire missions: needs an observer (spec/pitch 9c - enforced at issue time in orders.ts, not here), then
 * fires up to `rules.fireMission.volleys` volleys at a target cell offset each time by a seeded random
 * dispersion of up to `rules.fireMission.dispersionMaxM`. Damage applies to every alive unit within
 * `rules.fireMission.radiusM` of the dispersed impact point regardless of side - artillery cannot tell
 * friend from foe in an area of effect, so friendly fire is possible and is flagged on the event.
 */
function fireMissionPass(t: number, units: Unit[], grid: Grid, orders: OrderBook, rng: Rng, log: EventLog, state: CombatState, rules: Rules): void {
  const ordered = [...units].sort((a, b) => a.id - b.id);
  const fm = rules.fireMission;
  for (const order of orders.orders) {
    if (order.card !== 'fireMission' || order.status !== 'executing') continue;
    const unit = units.find((u) => u.id === order.unitId);
    if (!unit || !unit.alive || unit.ammo < fm.minAmmoToFire) continue;

    const volleysFired = state.fireMissionVolleys.get(order.id) ?? 0;
    if (volleysFired >= fm.volleys) continue;
    const scheduledTick = order.execStart + volleysFired * TICKS_PER_MIN;
    if (t < scheduledTick) continue;
    if (order.target.cell === undefined) continue; // fireMission is targetKind 'point'; isLegal enforced this at issue time

    const targetCell = order.target.cell;
    const calledCenter = grid.centerCm(targetCell);
    const dispersionCm = fm.dispersionMaxM * 100;
    const dxCm = Math.round((rng.float() * 2 - 1) * dispersionCm);
    const dyCm = Math.round((rng.float() * 2 - 1) * dispersionCm);
    const impact = { x: calledCenter.x + dxCm, y: calledCenter.y + dyCm };
    const dispersionM = Math.round(Math.sqrt(dxCm * dxCm + dyCm * dyCm) / 100);

    const def = UNIT_DEFS[unit.type];
    const missionDmg = def.firepower * fm.damageMultiplier;
    const strengthLoss = missionDmg * fm.strengthLossFraction;
    const suppressionGain = missionDmg * fm.suppressionGainFraction * rules.suppressionGainMultiplier;

    const hits: { unitId: number; dmg: number; side: Side }[] = [];
    for (const other of ordered) {
      if (!other.alive) continue;
      if (distM(grid, impact, other) > fm.radiusM) continue;
      other.strength = Math.max(0, other.strength - strengthLoss);
      other.suppression = Math.min(100, other.suppression + suppressionGain);
      other.firedThisMin = true;
      hits.push({ unitId: other.id, dmg: missionDmg, side: other.side });
    }
    const friendlyFire = hits.some((h) => h.side === unit.side);

    unit.ammo = Math.max(0, unit.ammo - fm.ammoCostPerVolley);
    const volley = volleysFired + 1;
    state.fireMissionVolleys.set(order.id, volley);
    log.push(
      t, 'fire.mission', order.side,
      { orderId: order.id, volley, cell: targetCell, impact, dispersionM, hits, friendlyFire },
      { unitId: order.unitId, cell: targetCell },
    );

    if (volley >= fm.volleys) {
      order.status = 'done';
      log.push(t, 'order.done', order.side, { card: 'fireMission' }, { unitId: order.unitId });
      if (unit.orderId === order.id) unit.orderId = -1;
      state.fireMissionVolleys.delete(order.id);
    }
  }
}

/**
 * Per-unit suppression decay/reset, pinning, the inContact idle-clear, morale, breaking, and
 * destruction. One pass in id order, as combat's other passes; all of these read state that
 * directFirePass/fireMissionPass just wrote this tick.
 */
function resolvePass(
  t: number, units: Unit[], grid: Grid, strengthBefore: Map<number, number>, log: EventLog, state: CombatState, rules: Rules,
): void {
  const ordered = [...units].sort((a, b) => a.id - b.id);
  for (const unit of ordered) {
    if (!unit.alive) continue;

    // Suppression decay, then reset the minute-scoped firedThisMin gate at the minute boundary
    // (after the decay check has used this tick's value, per the plan).
    if (!unit.firedThisMin) unit.suppression = Math.max(0, unit.suppression - rules.suppressionDecayPerTick);
    if (t % TICKS_PER_MIN === 0) unit.firedThisMin = false;

    if (unit.suppression > rules.pinnedThreshold && !unit.pinned) {
      unit.pinned = true;
      log.push(t, 'unit.pinned', unit.side, {}, { unitId: unit.id, significant: unit.side === 0 });
    } else if (unit.pinned && unit.suppression < rules.unpinnedThreshold) {
      unit.pinned = false;
      log.push(t, 'unit.unpinned', unit.side, {}, { unitId: unit.id });
    }

    // inContact idle-clear: neither fired nor was fired on (direct fire only) for CONTACT_IDLE_TICKS.
    const lf = state.lastFireTick.get(unit.id);
    if (unit.inContact && (lf === undefined || t - lf >= CONTACT_IDLE_TICKS)) {
      unit.inContact = false;
    }

    // Morale.
    const before = strengthBefore.get(unit.id) ?? unit.strength;
    const lossThisTick = Math.max(0, before - unit.strength);
    let morale = unit.morale - lossThisTick * rules.moraleLossPerDamagePoint;
    if (unit.suppression > 50) morale -= rules.moraleSuppressionPenalty;
    if (!hasFriendlyWithin1km(unit, units, grid)) morale -= rules.moraleIsolationPenalty;
    if (!unit.inContact) morale += rules.moraleRecoveryPerTick;
    unit.morale = Math.min(100, Math.max(0, morale));

    if (unit.morale < rules.breakThreshold && t >= unit.brokenUntil) {
      unit.brokenUntil = t + 300; // 30 sim minutes (TICKS_PER_MIN * 30)
      unit.posture = 'withdraw';
      unit.destCell = nearestFriendlyHqCell(unit, units, grid);
      log.push(t, 'unit.broken', unit.side, { destCell: unit.destCell }, { unitId: unit.id, cell: unit.destCell, significant: true });
    }

    if (unit.strength <= rules.destroyThreshold) {
      unit.alive = false;
      log.push(t, 'unit.destroyed', unit.side, {}, { unitId: unit.id, significant: true });
    }
  }
}

/**
 * Advances direct fire, fire-mission volleys, suppression/pinning, morale, and destruction by one tick.
 * See the plan's Task 10 and spec §4 "Combat", "Suppression", "Morale". Iterates units in id order
 * throughout (each internal pass re-sorts a copy so the order is stable and independent of caller order).
 * `state` (see `CombatState`/`newCombatState`) holds this Sim's volley counters and last-fire ticks; pass
 * the same instance across ticks of one Sim, and a fresh one per Sim (a live run, a replay, or a test).
 */
export function stepCombat(
  t: number,
  units: Unit[],
  grid: Grid,
  pictures: [Picture, Picture],
  orders: OrderBook,
  dials: Dials,
  rng: Rng,
  log: EventLog,
  state: CombatState,
  rules: Rules = DEFAULT_RULES,
): void {
  void dials; // reserved: no dial currently changes combat resolution directly (c2/ew act upstream, in orders/detection).
  const strengthBefore = new Map<number, number>();
  for (const u of units) strengthBefore.set(u.id, u.strength);

  directFirePass(t, units, grid, pictures, rng, state, rules);
  fireMissionPass(t, units, grid, orders, rng, log, state, rules);
  resolvePass(t, units, grid, strengthBefore, log, state, rules);
}
